import type { AuditEvent, RiskClass } from '@ralph/contracts'
import { pluralCategory } from '@/i18n/format.ts'
import type { Locale } from '@/i18n/locale.ts'

/**
 * `governance` — what is waiting on a human, and what the platform recorded.
 *
 * Extracted from `pages/GovernancePage.tsx`, which never had a catalogue at all (PRD-27
 * F-14, LOC-010). The sharing queue's own words are NOT here: they live in `sharing`,
 * because the same card is rendered from two places and one request has one wording.
 *
 * ── The register ──
 *
 * This is the reviewer's plane, so the mechanism vocabulary AGM-022 keeps out of the
 * conversation column is welcome here — `action` and `resourceType` are rendered as the
 * verbatim identifiers an auditor wants (D-8, LOC-043) and are deliberately absent from this
 * file. What IS here is every word the page says in its own voice, and none of it may
 * promise more than the record can prove: the failure state says "a problem reading the
 * record, not a record with nothing in it", because those two look identical on screen and
 * only one of them is an outage.
 *
 * English is the source of truth AND the type (LOC-011). Anything that interpolates is a
 * FUNCTION returning a whole sentence (LOC-013).
 */

/** See the note in `chat.ts`: structural, so this file does not close a circle on `I18n`. */
type Counting = { locale: Locale; formatCount: (value: number) => string }

const plural = (
  fmt: Counting,
  count: number,
  forms: Partial<Record<Intl.LDMLPluralRule, string>> & { other: string }
): string => forms[pluralCategory(fmt.locale, count)] ?? forms.other

/**
 * Every operation risk, as the word the badge shows.
 *
 * `satisfies Record<RiskClass, string>` is the whole point (D-9, LOC-018): a class added to
 * the contract becomes a compile error in both catalogues rather than a raw
 * `external_communication` on screen, which is what de-underscoring the enum used to
 * produce.
 *
 * The same enum is worded again in `activity`, and that is deliberate rather than a
 * duplication to fold: both are the operator plane and agree today, but a shared map is a
 * file every namespace writer has to edit and a translator handed one copy cannot see which
 * surface it is for.
 */
const risk = {
  read: 'read',
  reversible_write: 'reversible write',
  external_communication: 'external communication',
  privileged: 'privileged',
  financial: 'financial',
  destructive: 'destructive'
} satisfies Record<RiskClass, string>

/** The same, for what the platform decided. Three values, and a fourth would fail `tsc`. */
const outcome = {
  allow: 'allow',
  deny: 'deny',
  error: 'error'
} satisfies Record<AuditEvent['outcome'], string>

export const governance = {
  title: 'Governance',
  lead: 'Actions waiting on a decision, and the immutable record of what happened.',

  approvalsTab: 'Approvals',
  /** One entry for the tab and the panel heading: it is one thing, named once. */
  auditTrail: 'Audit trail',

  empty: {
    title: 'Nothing waiting',
    body: 'When the assistant proposes something consequential — sending a message, changing a system of record — it pauses here until a person decides.'
  },

  queueTitle: 'Waiting on a decision',
  /**
   * Lower case because it sits beside a glyph as a hint rather than opening a sentence. A
   * phrase needed both ways gets two entries; this one is only ever needed this way
   * (LOC-014).
   */
  queueHint: 'each one is decided in its conversation',

  risk,
  irreversible: 'irreversible',

  /**
   * Both halves arrive already formatted, and the sentence adds no preposition of its own:
   * Portuguese relative time carries one ("há 5 min.", "em 3 d"), so "requested at há 5 min."
   * is what a helpful-looking "at" would produce (LOC-022).
   */
  requestedAndExpires: (requested: string, expires: string): string => `requested ${requested} · expires ${expires}`,
  reviewInConversation: 'Review in conversation',

  auditLoading: 'Loading audit events…',
  auditFailedTitle: 'The audit trail could not be loaded',
  auditFailedBody:
    'This is a problem reading the record, not a record with nothing in it. Try again in a moment; if it keeps failing, tell whoever runs this deployment.',
  auditEmpty: {
    title: 'No audit events',
    body: 'Nothing has been recorded yet.'
  },

  /**
   * The endpoint asks for the last 100, so this counts what is on the screen and says so — a
   * bare total would claim the record ends here. English has one form ("shown" does not
   * inflect); Portuguese agrees with *eventos* and puts zero in the singular.
   */
  shown: (fmt: Counting, count: number): string =>
    plural(fmt, count, {
      one: `${fmt.formatCount(count)} shown`,
      other: `${fmt.formatCount(count)} shown`
    }),

  columns: {
    when: 'When',
    actor: 'Actor',
    action: 'Action',
    resource: 'Resource',
    outcome: 'Outcome'
  },
  outcome
}
